import React, { useEffect, useRef, useState } from "react";
import { isWelcomeGenAiContentMetricWidget } from "pages/Welcome/welcomeWidgetDataState";

/** Format seconds as localized duration (e.g. "3 dk", "2 dk 15 sn"). */
export function formatWelcomeWidgetDuration(seconds, t) {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value < 0) return "—";
  const totalSec = Math.max(0, Math.floor(value));
  if (totalSec < 60) {
    return t("welcome.durationSeconds", { count: totalSec });
  }
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (sec === 0) {
    if (min < 60) {
      return t("welcome.durationMinutes", { count: min });
    }
    const hrs = Math.floor(min / 60);
    const remMin = min % 60;
    if (remMin === 0) {
      return t("welcome.durationHours", { count: hrs });
    }
    return t("welcome.durationHoursMinutes", { hours: hrs, minutes: remMin });
  }
  return t("welcome.durationMinutesSeconds", { min, sec });
}

export function parseWelcomeJobTimestampMs(iso) {
  if (iso == null || iso === "") return null;
  let text = String(iso).trim();
  // API emits UTC; legacy naive ISO strings must not be parsed as browser local time.
  if (
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(text) &&
    !/[Zz]$/.test(text) &&
    !/[+-]\d{2}:\d{2}$/.test(text)
  ) {
    text = `${text}Z`;
  }
  const ms = new Date(text).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Elapsed seconds since job_created_at for in-progress widgets. */
export function getWelcomeWidgetProcessingElapsedSeconds(widget) {
  const start = parseWelcomeJobTimestampMs(widget?.job_created_at);
  if (start == null) return null;
  return Math.max(0, Math.floor((Date.now() - start) / 1000));
}

/** Elapsed seconds in queue (created → in_progress) while job is running. */
export function getWelcomeWidgetQueuedElapsedSeconds(widget) {
  const created = parseWelcomeJobTimestampMs(widget?.job_created_at);
  const inProgress = parseWelcomeJobTimestampMs(widget?.job_in_progress_at);
  if (created == null) return null;
  const endMs = inProgress != null ? inProgress : Date.now();
  return Math.max(0, Math.floor((endMs - created) / 1000));
}

/** Elapsed seconds actively processing (in_progress → now) while job is running. */
export function getWelcomeWidgetActiveProcessingElapsedSeconds(widget) {
  const inProgress = parseWelcomeJobTimestampMs(widget?.job_in_progress_at);
  if (inProgress == null) return null;
  return Math.max(0, Math.floor((Date.now() - inProgress) / 1000));
}

function hasWelcomeJobTimingSplit(widget) {
  const queued = Number(widget?.queued_duration_seconds);
  const processing = Number(widget?.processing_duration_seconds);
  return Number.isFinite(queued) && Number.isFinite(processing);
}

/** Resolved creation duration (seconds) for header badges; never returns 0. */
export function resolveWelcomeWidgetReadyDurationSeconds(widget) {
  const ready = Number(widget?.ready_duration_seconds);
  if (Number.isFinite(ready) && ready > 0) {
    return ready;
  }

  const queued = Number(widget?.queued_duration_seconds);
  const processing = Number(widget?.processing_duration_seconds);
  if (Number.isFinite(queued) && Number.isFinite(processing)) {
    const total = queued + processing;
    if (total > 0) return total;
  }

  const created = parseWelcomeJobTimestampMs(widget?.job_created_at);
  const inProgress = parseWelcomeJobTimestampMs(widget?.job_in_progress_at);
  const updated = parseWelcomeJobTimestampMs(widget?.job_updated_at);
  if (created != null && inProgress != null && updated != null && updated > inProgress) {
    const q = Math.max(0, Math.floor((inProgress - created) / 1000));
    const p = Math.max(0, Math.floor((updated - inProgress) / 1000));
    const total = (q > 0 ? q : inProgress > created ? 1 : 0) + (p > 0 ? p : 1);
    if (total > 0) return total;
  }
  if (created != null && updated != null && updated > created) {
    const total = Math.floor((updated - created) / 1000);
    return total > 0 ? total : 1;
  }

  return null;
}

/** Merge job timing fields returned by welcome/widget/data into a widget row. */
export function mergeWelcomeWidgetJobTimingFields(widgetRow, envelope) {
  if (!widgetRow || !envelope || typeof envelope !== "object") {
    return widgetRow;
  }
  const patch = {};
  [
    "job_created_at",
    "job_updated_at",
    "job_in_progress_at",
    "ready_duration_seconds",
    "queued_duration_seconds",
    "processing_duration_seconds",
  ].forEach((key) => {
    if (envelope[key] != null) {
      patch[key] = envelope[key];
    }
  });
  return Object.keys(patch).length ? { ...widgetRow, ...patch } : widgetRow;
}

export function widgetJobIsProcessing(widget) {
  const status = widget?.status;
  return status === "loading" || status === 1 || status === 2;
}

const DEFAULT_STUCK_IN_PROGRESS_MS = 5 * 60 * 1000;

function welcomeWidgetJobStatusLabel(status) {
  if (status === 1 || status === "pending") return "pending";
  if (status === 2 || status === "in_progress") return "in_progress";
  if (status === 3 || status === "completed") return "completed";
  if (status === 4 || status === "failed") return "failed";
  return status != null ? String(status) : null;
}

/** Anchor for stuck detection: current queue cycle only (never job_updated_at / widget created_at). */
export function resolveWelcomeJobStuckAnchorMs(widget) {
  const created = parseWelcomeJobTimestampMs(widget?.job_created_at);
  const inProgress = parseWelcomeJobTimestampMs(widget?.job_in_progress_at);
  if (inProgress != null && created != null && inProgress >= created) {
    return inProgress;
  }
  if (created != null) return created;
  return inProgress;
}

/** True when an in-progress job exceeded the expected duration (default 5 min). */
export function isWelcomeWidgetJobStuckInProgress(
  widget,
  thresholdMs = DEFAULT_STUCK_IN_PROGRESS_MS,
) {
  if (welcomeWidgetJobStatusLabel(widget?.status) !== "in_progress") {
    return false;
  }
  const anchorMs = resolveWelcomeJobStuckAnchorMs(widget);
  if (anchorMs == null) return false;
  return Date.now() - anchorMs >= thresholdMs;
}

/** Normalized worker progress fields from welcome/groups poll payload. */
export function getWelcomeWidgetJobProgressClassNames(styles) {
  return {
    wrap: styles.widgetJobProgressWrap,
    track: styles.widgetJobProgressTrack,
    bar: styles.widgetJobProgressBar,
    meta: styles.widgetJobProgressMeta,
    indeterminate: styles.widgetJobProgressIndeterminate,
    panelWrap: styles.widgetJobProgressPanelWrap,
    panelTrack: styles.widgetJobProgressPanelTrack,
    panelBar: styles.widgetJobProgressPanelBar,
    panelMeta: styles.widgetJobProgressPanelMeta,
  };
}

export function resolveWelcomeWidgetJobProgress(widget) {
  if (!widget) return null;
  const pctRaw = widget.job_progress_pct;
  const hasPct =
    pctRaw != null && pctRaw !== "" && Number.isFinite(Number(pctRaw));
  const pct = hasPct
    ? Math.max(0, Math.min(100, Math.floor(Number(pctRaw))))
    : null;
  const step = String(widget.job_progress_step || "").trim() || null;
  const currentRaw = widget.job_progress_current;
  const totalRaw = widget.job_progress_total;
  const current =
    currentRaw != null && Number.isFinite(Number(currentRaw))
      ? Math.floor(Number(currentRaw))
      : null;
  const total =
    totalRaw != null && Number.isFinite(Number(totalRaw))
      ? Math.floor(Number(totalRaw))
      : null;
  const jobId =
    widget.job_id != null && widget.job_id !== ""
      ? Number(widget.job_id)
      : null;
  return {
    pct,
    step,
    current,
    total,
    jobId: Number.isFinite(jobId) ? jobId : null,
  };
}

export function WelcomeWidgetJobProgressView({
  widget,
  t,
  variant = "compact",
  className,
  trackClassName,
  barClassName,
  metaClassName,
  indeterminateClassName,
}) {
  if (!widgetJobIsProcessing(widget)) return null;

  const progress = resolveWelcomeWidgetJobProgress(widget);
  const hasInProgressAt = widget?.job_in_progress_at != null;
  const pct = progress?.pct;
  const showDeterminate = pct != null && pct > 0;
  const showIndeterminate =
    hasInProgressAt && (pct == null || pct === 0) && !progress?.step;
  const showBar = showDeterminate || showIndeterminate || Boolean(progress?.step);

  if (!showBar && progress?.jobId == null) return null;

  const stepLabel =
    progress?.step ||
    (showIndeterminate
      ? t("welcome.widgetJobProgressPreparing")
      : hasInProgressAt
        ? t("welcome.widgetJobProgressWorking")
        : t("welcome.widgetJobProgressQueued"));

  const counter =
    progress?.current != null &&
    progress?.total != null &&
    progress.total > 0
      ? t("welcome.widgetJobProgressCounter", {
          current: progress.current,
          total: progress.total,
        })
      : null;

  const jobTrace =
    progress?.jobId != null
      ? t("welcome.widgetJobProgressJobId", { jobId: progress.jobId })
      : null;

  const pctLabel =
    showDeterminate && pct != null
      ? t("welcome.widgetJobProgressPct", { pct })
      : null;

  const metaParts = [jobTrace, stepLabel, counter, pctLabel].filter(Boolean);

  if (variant === "panel") {
    return (
      <div className={className} aria-live="polite">
        <div className={trackClassName}>
          <div
            className={
              showIndeterminate
                ? `${barClassName || ""} ${indeterminateClassName || ""}`.trim()
                : barClassName
            }
            style={{
              width: showDeterminate ? `${pct}%` : undefined,
            }}
          />
        </div>
        {metaParts.length ? (
          <div className={metaClassName}>
            {metaParts.map((part, idx) => (
              <span key={idx}>{part}</span>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={className} aria-live="polite">
      {showBar ? (
        <div className={trackClassName}>
          <div
            className={
              showIndeterminate
                ? `${barClassName || ""} ${indeterminateClassName || ""}`.trim()
                : barClassName
            }
            style={{
              width: showDeterminate ? `${pct}%` : undefined,
            }}
          />
        </div>
      ) : null}
      {metaParts.length ? (
        <div className={metaClassName}>
          {metaParts.map((part, idx) => (
            <span key={idx}>{part}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Live elapsed seconds while widget job is pending/in_progress. */
export function useWelcomeWidgetProcessingElapsed(widget) {
  const [elapsed, setElapsed] = useState(null);
  const [queuedElapsed, setQueuedElapsed] = useState(null);
  const [processingElapsed, setProcessingElapsed] = useState(null);

  useEffect(() => {
    if (!widget?.job_created_at || !widgetJobIsProcessing(widget)) {
      setElapsed(null);
      setQueuedElapsed(null);
      setProcessingElapsed(null);
      return undefined;
    }
    const tick = () => {
      setElapsed(getWelcomeWidgetProcessingElapsedSeconds(widget));
      const hasInProgressAt = widget?.job_in_progress_at != null;
      if (hasInProgressAt) {
        setQueuedElapsed(getWelcomeWidgetQueuedElapsedSeconds(widget));
        setProcessingElapsed(getWelcomeWidgetActiveProcessingElapsedSeconds(widget));
      } else {
        setQueuedElapsed(null);
        setProcessingElapsed(null);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [widget?.job_created_at, widget?.job_in_progress_at, widget?.status]);

  return { elapsed, queuedElapsed, processingElapsed };
}

/**
 * Seconds from when KPI/data fetch starts on this page until widget data is shown.
 * Must not use job_updated_at — that is when the backend job finished (possibly hours ago).
 */
export function useWelcomeWidgetPageLoadTiming(
  widget,
  isPageDataLoading,
  isWidgetVisible = true,
) {
  const [pageLoadDuration, setPageLoadDuration] = useState(null);
  const [pageLoadElapsed, setPageLoadElapsed] = useState(null);
  const fetchStartedAtRef = useRef(null);
  const completedRef = useRef(false);
  const widgetId = widget?.id;
  const isReady = widget?.status === "ready";

  useEffect(() => {
    fetchStartedAtRef.current = null;
    completedRef.current = false;
    setPageLoadDuration(null);
    setPageLoadElapsed(null);
  }, [widgetId]);

  useEffect(() => {
    if (!isReady || !isWidgetVisible) {
      return undefined;
    }

    if (completedRef.current) {
      return undefined;
    }

    // Start as soon as the widget is on screen; KPI may already be in RQ cache
    // (isPageDataLoading never true) — still record 0 sn rather than hiding timing.
    if (fetchStartedAtRef.current == null) {
      fetchStartedAtRef.current = Date.now();
    }

    if (isPageDataLoading) {
      const tick = () => {
        const start = fetchStartedAtRef.current;
        if (start == null) return;
        setPageLoadElapsed(
          Math.max(0, Math.floor((Date.now() - start) / 1000)),
        );
      };
      tick();
      const id = setInterval(tick, 1000);
      return () => clearInterval(id);
    }

    completedRef.current = true;
    setPageLoadDuration(
      Math.max(
        0,
        Math.floor((Date.now() - fetchStartedAtRef.current) / 1000),
      ),
    );
    setPageLoadElapsed(null);
    return undefined;
  }, [isReady, isPageDataLoading, isWidgetVisible, widgetId]);

  return { pageLoadDuration, pageLoadElapsed };
}

export function WelcomeWidgetStatusTiming({
  widget,
  t,
  className,
  isPageDataLoading = false,
  isWidgetVisible = true,
  hidePageLoadTiming = false,
  hideCreationTiming = false,
  pageLoadVariant = "header",
  progressClassNames = null,
}) {
  const { elapsed: aiElapsed, queuedElapsed: aiQueuedElapsed, processingElapsed: aiProcessingElapsed } =
    useWelcomeWidgetProcessingElapsed(widget);
  const readyDuration = resolveWelcomeWidgetReadyDurationSeconds(widget);
  const queuedDuration = Number(widget?.queued_duration_seconds);
  const processingDuration = Number(widget?.processing_duration_seconds);
  const { pageLoadDuration, pageLoadElapsed } = useWelcomeWidgetPageLoadTiming(
    widget,
    isPageDataLoading,
    isWidgetVisible || isPageDataLoading,
  );

  const isGenAiWidget = isWelcomeGenAiContentMetricWidget(widget);
  const lines = [];

  if (!hideCreationTiming) {
    if (widgetJobIsProcessing(widget) && aiElapsed != null) {
      if (
        aiQueuedElapsed != null &&
        aiProcessingElapsed != null
      ) {
        lines.push(
          t(
            isGenAiWidget
              ? "welcome.widgetAiCreationInProgressSplit"
              : "welcome.widgetCreationInProgressSplit",
            {
              queued: formatWelcomeWidgetDuration(aiQueuedElapsed, t),
              processing: formatWelcomeWidgetDuration(aiProcessingElapsed, t),
            },
          ),
        );
      } else {
        lines.push(
          t(
            isGenAiWidget
              ? "welcome.widgetAiCreationInProgress"
              : "welcome.widgetCreationInProgress",
            {
              duration: formatWelcomeWidgetDuration(aiElapsed, t),
            },
          ),
        );
      }
    }

    if (
      widget?.status === "ready" &&
      readyDuration != null &&
      Number.isFinite(readyDuration)
    ) {
      if (hasWelcomeJobTimingSplit(widget)) {
        lines.push(
          t(
            isGenAiWidget
              ? "welcome.widgetAiCreationDurationSplit"
              : "welcome.widgetCreationDurationSplit",
            {
              total: formatWelcomeWidgetDuration(readyDuration, t),
              queued: formatWelcomeWidgetDuration(queuedDuration, t),
              processing: formatWelcomeWidgetDuration(processingDuration, t),
            },
          ),
        );
      } else {
        lines.push(
          t(
            isGenAiWidget
              ? "welcome.widgetAiCreationDuration"
              : "welcome.widgetCreationDuration",
            {
              duration: formatWelcomeWidgetDuration(readyDuration, t),
            },
          ),
        );
      }
    }
  }

  if (!hidePageLoadTiming) {
    const pageLoadKey =
      pageLoadVariant === "footer"
        ? "welcome.widgetPageLoadFooter"
        : null;
    const pageLoadInProgressKey =
      pageLoadVariant === "footer"
        ? "welcome.widgetPageLoadFooterInProgress"
        : null;

    if (widget?.status === "ready" && isPageDataLoading && pageLoadElapsed != null) {
      const key = pageLoadInProgressKey ?? "welcome.widgetPageLoadInProgress";
      lines.push(
        t(key, {
          duration: formatWelcomeWidgetDuration(pageLoadElapsed, t),
        }),
      );
    } else if (
      widget?.status === "ready" &&
      !isPageDataLoading &&
      pageLoadDuration != null
    ) {
      const key = pageLoadKey ?? "welcome.widgetPageLoadDuration";
      lines.push(
        t(key, {
          duration: formatWelcomeWidgetDuration(pageLoadDuration, t),
        }),
      );
    }
  }

  const showProgress =
    isGenAiWidget &&
    widgetJobIsProcessing(widget) &&
    progressClassNames;

  if (!lines.length && !showProgress) return null;

  return (
    <div className={className}>
      {lines.map((line, idx) => (
        <span key={idx}>{line}</span>
      ))}
      {showProgress ? (
        <WelcomeWidgetJobProgressView
          widget={widget}
          t={t}
          variant="compact"
          className={progressClassNames.wrap}
          trackClassName={progressClassNames.track}
          barClassName={progressClassNames.bar}
          metaClassName={progressClassNames.meta}
          indeterminateClassName={progressClassNames.indeterminate}
        />
      ) : null}
    </div>
  );
}
